import { SupabaseClient } from "@supabase/supabase-js";

export type ComponentRouteTable =
  | "stayuptodate_components"
  | "embed_components";

function normalizeSlug(value: unknown): string {
  return String(value ?? "")
    .trim()
    .replace(/^\/+/, "")
    .replace(/\/+$/, "");
}

/** URL slug variants (slashes, casing) for matching `route_name` / `unique_id`. */
export function slugLookupVariants(raw: unknown): string[] {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed) return [];

  const normalized = normalizeSlug(trimmed);
  const lower = normalized.toLowerCase();
  const withLeadingSlash = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;

  const variants = new Set<string>([
    trimmed,
    normalized,
    lower,
    withLeadingSlash,
    normalizeSlug(withLeadingSlash),
  ]);

  return [...variants].filter(Boolean);
}

function rowMatchesSlug(row: any, variants: string[]): boolean {
  if (!row || !variants.length) return false;
  const routeNorm = normalizeSlug(row.route_name).toLowerCase();
  const routeRaw = String(row.route_name ?? "").trim().toLowerCase();
  const uidNorm = normalizeSlug(row.unique_id).toLowerCase();
  const uidRaw = String(row.unique_id ?? "").trim().toLowerCase();
  const nameNorm = normalizeSlug(row.name).toLowerCase();
  const displayNorm = normalizeSlug(row.display_name).toLowerCase();

  return variants.some((variant) => {
    const v = normalizeSlug(variant).toLowerCase();
    if (!v) return false;
    if (
      v === routeNorm ||
      v === routeRaw ||
      v === uidNorm ||
      v === uidRaw ||
      v === nameNorm ||
      v === displayNorm
    ) {
      return true;
    }
    const routeLast = routeNorm.split("/").filter(Boolean).pop() || "";
    const uidLast = uidNorm.split("/").filter(Boolean).pop() || "";
    return v === routeLast || v === uidLast;
  });
}

function ownerScopedQuery(
  client: SupabaseClient,
  table: ComponentRouteTable,
  user_id: string,
  username?: string,
) {
  let q = client.from(table).select("*").neq("status", "deleted");
  if (user_id) q = q.eq("user_id", user_id);
  else if (username) q = q.eq("username", username);
  else return null;
  return q;
}

async function fetchRowsForOwner(
  client: SupabaseClient,
  table: ComponentRouteTable,
  user_id: string,
  username?: string
): Promise<any[]> {
  if (user_id) {
    const { data, error } = await client
      .from(table)
      .select("*")
      .eq("user_id", user_id)
      .neq("status", "deleted");

    if (error) throw error;
    return Array.isArray(data) ? data : [];
  }

  if (username) {
    const { data, error } = await client
      .from(table)
      .select("*")
      .eq("username", username)
      .neq("status", "deleted");

    if (error) throw error;
    return Array.isArray(data) ? data : [];
  }

  return [];
}

/**
 * Find a carousel row by public route slug (includes archived components).
 * Tries indexed route_name / unique_id first; falls back to owner scan.
 */
export async function findComponentByRouteSlug(
  client: SupabaseClient,
  table: ComponentRouteTable,
  slug: string,
  user_id: string,
  username?: string
): Promise<any | undefined> {
  const variants = slugLookupVariants(slug);
  if (!variants.length) return undefined;

  const makeScoped = () => ownerScopedQuery(client, table, user_id, username);
  if (!makeScoped()) return undefined;

  // Fast path: exact unique_id / route_name (indexed).
  for (const variant of variants) {
    const scoped = makeScoped();
    if (!scoped) return undefined;
    const { data, error } = await scoped
      .or(`unique_id.eq.${variant},route_name.eq.${variant}`)
      .limit(5);
    if (error) throw error;
    const hit = (Array.isArray(data) ? data : []).find((row) =>
      rowMatchesSlug(row, variants),
    );
    if (hit) return hit;
  }

  // Case-insensitive / name match (still limited).
  const primary = normalizeSlug(slug);
  if (primary) {
    const scoped = makeScoped();
    if (!scoped) return undefined;
    const { data: fuzzy, error: fuzzyError } = await scoped
      .or(
        `route_name.ilike.${primary},unique_id.ilike.${primary},name.ilike.${primary},display_name.ilike.${primary}`,
      )
      .limit(20);
    if (fuzzyError) throw fuzzyError;
    const fuzzyHit = (Array.isArray(fuzzy) ? fuzzy : []).find((row) =>
      rowMatchesSlug(row, variants),
    );
    if (fuzzyHit) return fuzzyHit;
  }

  // Last resort: owner scan (legacy rows with odd route shapes).
  const rows = await fetchRowsForOwner(client, table, user_id, username);
  return rows.find((row) => rowMatchesSlug(row, variants));
}

/**
 * Resolve a public carousel for embed or link-in-bio views.
 * Embed prefers `embed_components` but falls back to `stayuptodate_components`
 * so archived / legacy rows copied from link-in-bio still open on `/embed/...` URLs.
 */
export async function resolvePublicComponentByRouteSlug(
  client: SupabaseClient,
  slug: string,
  user_id: string,
  from?: string,
  username?: string
): Promise<any | undefined> {
  const variants = slugLookupVariants(slug);
  if (!variants.length) return undefined;

  const tables: ComponentRouteTable[] =
    from === "embed-search"
      ? ["embed_components", "stayuptodate_components"]
      : ["stayuptodate_components", "embed_components"];

  for (const table of tables) {
    const row = await findComponentByRouteSlug(
      client,
      table,
      slug,
      user_id,
      username
    );
    if (row) return row;
  }

  // Last-resort fallback: if slug is a pure numeric string, try looking up by
  // database id. This handles legacy embed components that were created before
  // unique_id/route_name were auto-generated, where the URL slug falls back to
  // the component's numeric id.
  if (/^\d+$/.test(slug.trim())) {
    const numericId = Number(slug.trim());
    for (const table of tables) {
      const ownerFilter = user_id
        ? { field: "user_id", value: user_id }
        : username
        ? { field: "username", value: username }
        : null;
      if (!ownerFilter) continue;
      const { data, error } = await client
        .from(table)
        .select("*")
        .eq(ownerFilter.field, ownerFilter.value)
        .eq("id", numericId)
        .neq("status", "deleted")
        .limit(1);
      if (!error && data && data.length > 0) return data[0];
    }
  }

  return undefined;
}
